import { useCallback } from "react";
import { type Column, headerLabel, useTableExport } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import { downloadBlob, printTable, generateFilename } from "~/shared/lib/export-utils";
import { DateValue } from "~/shared/lib/analytics-utils";

interface UseReportExportConfig<T> {
  data: T[];
  columns: Column<T>[];
  reportName: string;
  startDate?: DateValue | string;
  endDate?: DateValue | string;
}

const pad = (n: number) => String(n).padStart(2, "0");
const displayDate = (date: DateValue | string) =>
  typeof date === "string" ? date : `${pad(date.day)}.${pad(date.month)}.${date.year}`;

// Numbers rounded like the tables show them; React nodes and objects left blank.
const pdfCellText = (value: unknown): string => {
  if (typeof value === "number") return String(Math.round(value * 100) / 100);
  if (typeof value === "string") return value;
  if (typeof value === "boolean" || typeof value === "bigint") return String(value);
  return "";
};

const columnValue = <T>(row: T, column: Column<T>, index: number): unknown => {
  if (typeof column.accessor === "function") return column.accessor(row, index);
  if (column.key) return (row as Record<string, unknown>)[column.key];
  return "";
};

/**
 * Excel, PDF and print for the Отчёты tables. The Excel file comes from `useTableExport`
 * in `@racoon/ui`, as on the other pages; the PDF and print add the period and filters.
 */
export function useReportExport<T>(config: UseReportExportConfig<T>) {
  const { data, columns, reportName, startDate, endDate } = config;
  const t = useT();
  // The localized "Period: from – to" line, shared by the print and PDF headers.
  const period =
    startDate && endDate
      ? t(Namespace.Common, "pdfPeriod", {
          from: displayDate(startDate),
          to: displayDate(endDate),
        })
      : "";

  const { handleExportExcel } = useTableExport({
    data,
    columns,
    reportName,
    startDate: startDate?.toString(),
    endDate: endDate?.toString(),
    errorMessage: t(Namespace.Analytics, "buttons.excelFailed"),
  });

  const handlePrint = useCallback(
    (filterInfo?: string) =>
      printTable({
        data,
        columns,
        title: reportName,
        period: period || undefined,
        filterInfo,
      }),
    [data, columns, reportName, period],
  );

  const handleExportPdf = useCallback(
    async (filterInfo?: string) => {
      // Loaded on demand: the PDF renderer is large.
      const { generateTablePdfBlob } = await import("~/shared/lib/table-pdf.export");
      const blob = await generateTablePdfBlob({
        title: reportName,
        subtitle: [period, filterInfo ?? ""].filter(Boolean),
        columns: columns.map((column) => ({
          header: headerLabel(column),
          width: typeof column.width === "number" ? column.width : 120,
          align: column.align,
        })),
        rows: data.map((row, index) =>
          columns.map((column) => pdfCellText(columnValue(row, column, index))),
        ),
        pageLabel: (page, total) => t(Namespace.Common, "pdfPage", { page, total }),
      });
      const filename = generateFilename(
        reportName.toLowerCase().replace(/\s+/g, "_"),
        startDate?.toString(),
        endDate?.toString(),
        ".pdf",
      );
      downloadBlob(blob, filename);
    },
    [data, columns, reportName, startDate, endDate, period, t],
  );

  return { handleExportExcel, handleExportPdf, handlePrint };
}
